import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { environment } from 'src/environments/environment'
import { AppState } from '../ngrx-store/app.state'
import { CorpMemberState } from '../../app/ngrx-store/app.state'
import { io, Socket } from 'socket.io-client'
import { Observable } from 'rxjs'
import { share } from 'rxjs/operators'
import { IOEventName } from '../utils/types'

@Injectable({
    providedIn: 'root',
})
export class SocketIoService {
    corpMember!: CorpMemberState

    private socket: Socket

    // One multicasted Observable per event name, shared across every consumer — a shared()
    // subscription means exactly one real socket.on() registration exists per event no matter
    // how many effects/components subscribe, and unsubscribing one consumer never tears down
    // another's (see onEvent() below).
    private eventStreams = new Map<IOEventName, Observable<any>>()

    constructor(
        public http: HttpClient,
        public store: Store<AppState>,
    ) {
        this.store.pipe(select('corper')).subscribe({
            next: (value) => {
                this.corpMember = value
            },
        })

        // autoConnect: false — the socket object exists immediately (safe for effects to attach
        // .on() listeners to at app bootstrap, before login), but no network connection opens
        // until connect() is explicitly called (see CallerService.connectSockets()).
        this.socket = io(environment.basehost, {
            transports: ['websocket', 'polling'],
            autoConnect: false,
        })

        this.socket.on(IOEventName.CONNECT, () => {
            console.log('WebSocket connected to', environment.basehost);
        });

        this.socket.on(IOEventName.DISCONNECT, () => {
            console.log('WebSocket disconnected from', environment.basehost);
        });
    }

    /**
     * Idempotent — safe to call on every login/app-boot; a no-op if already connected.
     * Refreshes the query params to the current corpMember identity before connecting, so a
     * reconnect after a different user logs in in the same tab doesn't carry stale identity.
     */
    public connect(): void {
        if (this.socket.connected) return

        this.socket.io.opts.query = {
            state_code: this.corpMember?.state_code,
            id: this.corpMember?.id,
        }
        this.socket.connect()
    }

    public onEvent<T>(event: IOEventName): Observable<T> {
        if (!this.eventStreams.has(event)) {
            const stream$ = new Observable<T>((observer) => {
                const handler = (data: T) => observer.next(data)
                this.socket.on(event, handler)
                return () => this.socket.off(event, handler)
            }).pipe(share())
            this.eventStreams.set(event, stream$)
        }
        return this.eventStreams.get(event)!
    }

    /**
     * Closes the transport only — deliberately does NOT call removeAllListeners(). Listeners
     * registered via onEvent() (e.g. SocketEffects) are meant to live for the app's session and
     * must still be attached when connect() reopens the transport on the next login.
     */
    public destroy() {
        if (this.socket) {
            this.socket.disconnect()
        }
    }
}
